<script setup>
import AppCard from "@/components/AppCard";
import AppButton from "@/components/AppButton";
import AppRow from "@/components/AppRow";
import AppAlert from "@/components/AppAlert";
import { ref, reactive, computed, onMounted, defineProps, defineEmits } from "vue";
import { GoogleMap, Marker } from "vue3-google-map";
import { store } from "@/store";

defineProps({
  state: { type: Object, default: () => ({}) },
});

const emit = defineEmits(["next", "previous"]);

const MAPS_API_KEY = process.env.VUE_APP_GOOGLE_MAPS_API_KEY;

const mapRef = ref(null);
const locating = ref(false);

const center = reactive({ lat: 20.2251652, lng: -87.4456273 });

const markerOptions = reactive({
  draggable: true,
  position: { lat: 0, lng: 0 },
});

const hasLocation = computed(() => markerOptions.position.lat !== 0);

async function reverseGeocode(lat, lng) {
  try {
    const res = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${MAPS_API_KEY}`
    );
    const data = await res.json();
    if (data.status !== "OK" || !data.results.length) return null;

    const components = data.results[0].address_components;
    const get = (type) =>
      components.find((c) => c.types.includes(type))?.long_name ?? "";

    return {
      street: `${get("route")} ${get("street_number")}`.trim(),
      city: get("locality") || get("sublocality_level_1"),
      state: get("administrative_area_level_1"),
      country: get("country"),
      zip: get("postal_code"),
      formatted: data.results[0].formatted_address,
    };
  } catch {
    return null;
  }
}

async function saveLocation(lat, lng) {
  markerOptions.position = { lat, lng };

  // Guardar lat/lng de inmediato sin esperar el geocoding
  const base = { lat, lng, address: null };
  store.map_location = base;
  localStorage.setItem("map_location", JSON.stringify(base));

  // Enriquecer con dirección de forma asíncrona
  const address = await reverseGeocode(lat, lng);
  if (address) {
    const full = { lat, lng, address };
    store.map_location = full;
    localStorage.setItem("map_location", JSON.stringify(full));
  }
}

function handleMapClick(e) {
  if (!e?.latLng) return;
  saveLocation(e.latLng.lat(), e.latLng.lng());
}

function handleMarkerDrag(e) {
  if (!e?.latLng) return;
  saveLocation(e.latLng.lat(), e.latLng.lng());
}

function detectLocation() {
  if (!navigator.geolocation) return;
  locating.value = true;
  navigator.geolocation.getCurrentPosition(
    (position) => {
      const lat = position.coords.latitude;
      const lng = position.coords.longitude;
      center.lat = lat;
      center.lng = lng;
      saveLocation(lat, lng);
      locating.value = false;
    },
    (error) => {
      console.error(error.message);
      locating.value = false;
    }
  );
}

onMounted(() => {
  detectLocation();
});

function nextStep() {
  emit("next", { ...markerOptions });
}

function backStep() {
  emit("previous");
}
</script>

<template lang="pug">
AppCard
  h1.text-center.title-step {{ $t('wizard_contract.steps.map') }}

  AppAlert.mb-4(
    type="info"
    :title="$t('wizard_contract.map.instruction')"
    v-if="!hasLocation && !locating"
  )

  .location-bar.mb-3
    .location-info(v-if="hasLocation")
      svg(xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor")
        path(fill-rule="evenodd" d="M9.69 18.933l.003.001C9.89 19.02 10 19 10 19s.11.02.308-.066l.002-.001.006-.003.018-.008a5.741 5.741 0 00.281-.14c.186-.096.446-.24.757-.433.62-.384 1.445-.966 2.274-1.765C15.302 14.988 17 12.493 17 9A7 7 0 103 9c0 3.492 1.698 5.988 3.355 7.584a13.731 13.731 0 002.273 1.765 11.842 11.842 0 00.976.544l.062.029.018.008.006.003zM10 11.25a2.25 2.25 0 100-4.5 2.25 2.25 0 000 4.5z" clip-rule="evenodd")
      span {{ markerOptions.position.lat.toFixed(6) }}, {{ markerOptions.position.lng.toFixed(6) }}

    button.locate-btn(@click="detectLocation" :disabled="locating" :class="{ loading: locating }")
      svg(v-if="!locating" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor")
        path(fill-rule="evenodd" d="M11.54 22.351l.07.04.028.016a.76.76 0 00.723 0l.028-.015.071-.041a16.975 16.975 0 001.144-.742 19.58 19.58 0 002.683-2.282c1.944-2.083 3.711-5.14 3.711-9.327 0-4.97-4.03-9-9-9s-9 4.03-9 9c0 4.187 1.767 7.244 3.71 9.327a19.577 19.577 0 002.684 2.282 16.974 16.974 0 001.144.742zM12 13.5a3 3 0 100-6 3 3 0 000 6z" clip-rule="evenodd")
      svg.spin(v-else xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24")
        circle.opacity-25(cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4")
        path.opacity-75(fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z")
      | {{ locating ? $t('wizard_contract.map.locating') : $t('wizard_contract.map.locate_btn') }}

  GoogleMap.map-container(
    ref="mapRef"
    :api-key="MAPS_API_KEY"
    :center="center"
    :zoom="15"
    :key="center.lat"
    @click="handleMapClick"
  )
    Marker(
      v-if="hasLocation"
      :options="markerOptions"
      :key="markerOptions.position.lat"
      @dragend="handleMarkerDrag"
    )

  AppRow.mt-5.btns-steps
    AppButton.mb-0.bg-gignetgreen-500(:label="$t('wizard_contract.address.btn_back')" @click="backStep")
    AppButton.mb-0.bg-gignetgreen-500(:label="$t('wizard_contract.address.btn_send')" :disabled="!hasLocation" @click="nextStep")
</template>

<style lang="scss" scoped>
.bg-gignetgreen-500 {
  background-color: rgba(21, 199, 208, var(--tw-bg-opacity));
  &:hover {
    background-color: rgba(18, 172, 180, var(--tw-bg-opacity));
  }
}

.title-step {
  font-weight: bold;
  color: #2a5aad;
  margin-bottom: 16px;
}

.map-container {
  width: 100%;
  height: 450px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.12);
}

.location-bar {
  @apply flex items-center justify-between gap-3;
  min-height: 36px;
}

.location-info {
  @apply flex items-center gap-1 text-sm font-medium;
  color: #2a5aad;
  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
}

.locate-btn {
  @apply flex items-center gap-2 ml-auto;
  @apply text-sm font-semibold px-4 py-2 rounded-lg;
  @apply transition-all duration-200;
  color: #2a5aad;
  border: 1.5px solid #2a5aad;
  background: white;

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  &:hover:not(:disabled) {
    background: #2a5aad;
    color: white;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.btns-steps {
  margin-top: 20px;
}
</style>
